import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

/**
 * BusinessCancelDialog Component
 * Dialog for business to cancel items with options for partial/full cancellation
 */
const BusinessCancelDialog = ({
  open,
  onClose,
  dialogData,
  onSubmit,
}) => {
  const [cancelType, setCancelType] = useState("partial");
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState("yes");


  const handleCancelTypeChange = (event) => {
    setCancelType(event.target.value);
  };

  const handleRestockChange = (event) => {
    setRestock(event.target.value);
  };

  const handleSubmit = () => {
    let payload;
    
    if (cancelType === "full") {
      // Full cancel - approve all pending cancellations as customer requested
      payload = {
        cancelType: "full_order",
        action: "force_cancel_all",
        restock: restock === "yes",
        reason: reason || "No reason provided",
        decisions: [] // EMPTY - approve all pending cancellations as customer requested
      };
    } else {
      // Partial cancel - custom business decision
      payload = {
        cancelType: "partial_items",
        action: "approve_all",
        restock: restock === "yes",
        reason: reason || "No reason provided",
        decisions: dialogData?.selectedItemsData || [] // Use actual selected items data
      };
    }
    
    onSubmit(payload);
    handleClose();
  };

  const handleClose = () => {
    setCancelType("partial");
    setReason("");
    setRestock("yes");
    onClose();
  };

  // Calculate total items and quantity from selectedItemsData
  // Count unique cartItemIds (one item can have multiple variants)
  const uniqueCartItemIds = new Set(dialogData?.selectedItemsData?.map(item => item.cartItemId) || []);
  const totalItems = uniqueCartItemIds.size || 0;
  const totalQuantity = dialogData?.selectedItemsData?.reduce((sum, item) => sum + (item.quantityToCancel || 0), 0) || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 10002, 
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: "1px solid #E0E0E0", 
        pb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
          cancel
        </Typography>
        <IconButton 
          size="small" 
          onClick={handleClose}
          sx={{ ml: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Cancellation Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
          <RadioGroup
            value={cancelType}
            onChange={handleCancelTypeChange}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="partial"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  partial cancel ({totalItems} {totalItems === 1 ? 'item' : 'items'}, qty. {totalQuantity})
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            <FormControlLabel
              value="full"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  full cancel (select and cancel all items)
        </Typography>
              }
              sx={{ ml: 0 }}
            />
          </RadioGroup>
        </FormControl>

        {/* Reason for Cancel */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 1, 
              textTransform: "lowercase",
              fontWeight: 500
            }}
          >
            reason for cancel (optional):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="enter reason for cancellation..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                textTransform: "lowercase",
                "&::placeholder": {
                  textTransform: "lowercase",
                  opacity: 0.7,
                },
              },
            }}
          />
        </Box>

        {/* Restock Question */}
        <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2,
              textTransform: "lowercase",
              fontWeight: 500
            }}
          >
            would you like to restock the selected items in the inventory?
          </Typography>
          <RadioGroup
            value={restock}
            onChange={handleRestockChange}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="yes"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  yes
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            <FormControlLabel
              value="no"
              control={<Radio sx={{ color: "#7C3AED", "&.Mui-checked": { color: "#7C3AED" } }} />}
              label={
                <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                  no
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          </RadioGroup>
        </FormControl>

        {/* Information Text */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
                sx={{
            display: "block",
            mt: 2,
            textAlign: "center",
            textTransform: "lowercase"
          }}
        >
          items that are already being made cannot be cancelled
                    </Typography>
      </DialogContent>

      <DialogActions sx={{ borderTop: "1px solid #E0E0E0", p: 2, justifyContent: "center" }}>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ 
            textTransform: "lowercase", 
            minWidth: 120,
            backgroundColor: "#7C3AED",
            "&:hover": {
              backgroundColor: "#6d28d9"
            }
          }}
        >
          cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessCancelDialog;
